import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";
import Button from "react-bootstrap/Button";
import { useForm } from "react-hook-form";
import { animated, useSpring } from "react-spring";
import { pushGTMAnalyticsEvent } from "../../../modules/pushGTMAnalyticsEvent";
import { items_state, update_items_activeIds } from "../../dataManagers/GlobalDataManagers";
import { InfoIcon } from "../InfoIcon/InfoIcon";
import { INFO_CONTENT } from "../InfoIcon/infoContent";
import "./styles.scss";

const PATCH_TEXT_PLACEHOLDER = "Add Text";

export const InputBox = ({ component, additionalPrice = 0, ...props }) => {
  const [itemsState] = useAtom(items_state);
  const [, setItemsActiveIds] = useAtom(update_items_activeIds);

  const inputName = props.name;
  const { register, handleSubmit, setValue, getValues } = useForm({ mode: "onBlur" });
  const [animationProps, animate] = useSpring(() => ({
    from: { opacity: 0, scale: 0 },
  }));
  const customTextItem = useMemo(() => itemsState.array.find((item) => item._id === props.itemId), [component._id]);

  const maxCharLength = props.type == "number" ? 3 : 15;

  useEffect(() => {
    animate.start({
      from: { opacity: 0, scale: 0 },
      to: { opacity: 1, scale: 1 },
    });

    // set inputbox to applied text, or show placeholder copy without persisting it to state
    const appliedText = itemsState.activeObjs[component._id].inputs?.text_input ?? itemsState.activeObjs[component._id].text_input ?? "";
    setValue(inputName, appliedText || PATCH_TEXT_PLACEHOLDER);

    // seed the activeItemIds with default inputs
    const copy = { ...itemsState.activeIds };
    if (copy[component._id].inputs?.text_input === customTextItem?.inputs?.text_input) return;
    let newActiveInput = {};
    newActiveInput._id = customTextItem._id;
    newActiveInput.inputs = customTextItem.inputs;
    copy[component._id] = newActiveInput;
    setItemsActiveIds(copy);
  }, [component._id]);

  const onFocusInput = () => {
    if (getValues(inputName) === PATCH_TEXT_PLACEHOLDER) {
      setValue(inputName, "");
    }
  };

  const onBlurForm = () => {
    handleSubmit(onSubmitForm)();

    const appliedText = itemsState.activeObjs[component._id].inputs?.text_input ?? "";
    const currentValue = getValues(inputName);
    if (!appliedText && (!currentValue || currentValue.trim() === "")) {
      setValue(inputName, PATCH_TEXT_PLACEHOLDER);
    }
  };

  const onSubmitForm = (data) => {
    // abort if no text was entered, placeholder is unchanged, or text matches the applied value
    if (data[inputName].length == 0 || data[inputName] === PATCH_TEXT_PLACEHOLDER || customTextItem.inputs.text_input === data[inputName]) {
      return;
    }

    const copy = { ...itemsState.activeIds };
    let newActiveInput = {};

    newActiveInput._id = customTextItem._id;
    newActiveInput.inputs = customTextItem.inputs;

    newActiveInput.inputs.text_input = data[inputName];

    copy[component._id] = newActiveInput;
    setItemsActiveIds(copy);

    pushGTMAnalyticsEvent({
      event: "Apply Custom Text - Thrill",
      itemSection: component._id,
      page: component.category,
    });
  };

  return (
    <animated.div className="InputBox" style={animationProps}>
      {/* onSubmit is used when enter key is pressed */}
      <form className="p-3" onSubmit={handleSubmit(onSubmitForm)} onBlur={onBlurForm}>
        <div className="form-group">
          <div className="d-flex">
            {props.label && (
              <label htmlFor={inputName} className="d-flex align-items-center">
                {props.label}
                {props.label === "Custom Text" && (
                  <InfoIcon title={INFO_CONTENT.CUSTOM_TEXT.title} content={INFO_CONTENT.CUSTOM_TEXT.content} className="ms-2" />
                )}
              </label>
            )}
          </div>
          <div className="input-container">
            <input
              className="mb-3 form-control"
              name={inputName}
              type={props.type}
              maxLength={maxCharLength}
              onFocus={onFocusInput}
              onInput={(e) => {
                e.target.value = e?.target?.value?.slice(0, maxCharLength);
              }} // workaround for maxLength not working on 'number' inputs
              {...register(inputName, {
                required: false,
                maxLength: maxCharLength,
              })}
            />
            <img src="/images/iconTextBox.svg" alt="Text Box" />
          </div>
        </div>

        <div className="d-flex justify-content-start">
          <Button variant="outline-danger">Apply</Button>
        </div>
      </form>
    </animated.div>
  );
};
